import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Ticket, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { api } from '../../App';
import { EventShareButtons } from './EventPromo';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const daysUntil = (iso) => {
  if (!iso) return null;
  const start = new Date(iso); const now = new Date();
  const diff = Math.ceil((new Date(start.getFullYear(), start.getMonth(), start.getDate()) - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
  return diff;
};

/** Home-page "billboard" for the featured (or next) event: flyer + dates + Buy Tickets + share. Admin can switch it off. */
export function FeaturedEventBanner() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/api/events/featured-banner').then(setData).catch(() => setData({ event: null })); }, []);
  const ev = data?.event;
  if (!ev) return null;
  const d = daysUntil(ev.event_days?.[0] ? `${ev.event_days[0]}T12:00:00` : ev.date);
  const countdown = d === 0 ? 'Today' : d === 1 ? 'Tomorrow' : d > 1 ? `In ${d} days` : null;
  const flyer = ev.flyer_preview_url ? `${BACKEND}${ev.flyer_animated ? ev.flyer_url : ev.flyer_preview_url}` : (ev.image_url || null);

  return (
    <section className="py-8 sm:py-10 bg-[hsl(var(--lux-ivory,var(--background)))]" data-testid="featured-event-banner">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45 }}
          className="relative overflow-hidden rounded-3xl border-2 border-[hsl(var(--lux-gold,var(--primary))/0.6)] bg-[hsl(var(--lux-royal,var(--gs-navy)))] text-white shadow-xl">
          <div className="absolute inset-0 opacity-[0.08] lux-bandhani-dots pointer-events-none" />
          <div className="relative grid md:grid-cols-[minmax(0,320px)_1fr] gap-6 p-5 sm:p-7 items-center">
            {flyer && (
              <Link to={`/events/${ev.event_id}`} className="block rounded-2xl overflow-hidden bg-white/10 ring-1 ring-white/20 max-h-[26rem]" data-testid="featured-event-flyer">
                <img src={flyer} alt={`${ev.title} flyer`} className="w-full h-full max-h-[26rem] object-contain" loading="lazy" />
              </Link>
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge className="bg-[hsl(var(--lux-gold,var(--primary)))] text-[hsl(var(--lux-ink,var(--gs-ink)))] hover:bg-[hsl(var(--lux-gold,var(--primary)))]">Featured event</Badge>
                {countdown && <Badge variant="outline" className="border-white/40 text-white" data-testid="featured-event-countdown">{countdown}</Badge>}
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold leading-tight" style={{ fontFamily: 'Cormorant Garamond, Space Grotesk, serif' }} data-testid="featured-event-title">{ev.title}</h2>
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-white/85">
                <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {ev.when}</span>
                {ev.venue && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {ev.venue}</span>}
              </div>
              {ev.description && <p className="mt-3 text-white/80 text-sm sm:text-base line-clamp-3 max-w-2xl">{ev.description}</p>}
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Link to={`/events/${ev.event_id}`} data-testid="featured-event-tickets">
                  <Button size="lg" className="bg-[hsl(var(--lux-gold,var(--primary)))] text-[hsl(var(--lux-ink,var(--gs-ink)))] hover:brightness-110 font-semibold"><Ticket className="w-4 h-4 mr-2" /> Buy tickets</Button>
                </Link>
                <Link to={`/events/${ev.event_id}`} className="text-sm underline underline-offset-4 text-white/90 inline-flex items-center gap-1" data-testid="featured-event-details">Event details <ArrowRight className="w-4 h-4" /></Link>
              </div>
              <div className="mt-4 [&_button]:bg-white/10 [&_button]:text-white [&_button]:border-white/30 [&_button:hover]:bg-white/20">
                <EventShareButtons eventId={ev.event_id} compact />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
